import { ClipboardEvent, KeyboardEvent, RefObject, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { insertAtSelection } from '../utils/insertAtSelection';
import { ComposerAttachment, ComposerAttachments, useComposerAttachments } from './useComposerAttachments';

export const MAX_MESSAGE_LENGTH = 10_000;
const MESSAGE_LENGTH_COUNTER_THRESHOLD = 9_000;

export interface OutgoingDraft {
    text: string;
    attachments: ComposerAttachment[];
}

export interface OutgoingDraftHandlers {
    onAttachmentsRejected(): void;
}

interface Params {
    isDisabled: boolean;
    isSending: boolean;
    onSend(draft: OutgoingDraft, handlers: OutgoingDraftHandlers): boolean;
}

export interface SupportComposer extends ComposerAttachments {
    text: string;
    textareaRef: RefObject<HTMLTextAreaElement | null>;
    isDisabled: boolean;
    isCounterVisible: boolean;
    isTextTooLong: boolean;
    canSend: boolean;
    changeText(text: string): void;
    insertEmoji(emoji: string): void;
    send(): Promise<void>;
    handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void;
    handlePaste(event: ClipboardEvent<HTMLElement>): void;
}

export function useSupportComposer({ isDisabled, isSending, onSend }: Params): SupportComposer {
    const attachments = useComposerAttachments();
    const [text, setText] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const isTextTooLong = text.length > MAX_MESSAGE_LENGTH;
    const canSend =
        !isDisabled &&
        !isSending &&
        !attachments.isUploading &&
        !attachments.hasFailedAttachments &&
        !isTextTooLong &&
        (text.trim() !== '' || attachments.attachments.length > 0);

    const send = async (): Promise<void> => {
        if (!canSend) return;
        const preparedAttachments = await attachments.prepareForSending();
        if (!preparedAttachments) return;

        const draftText = text;
        const isAccepted = onSend(
            { text: draftText.trim(), attachments: preparedAttachments },
            {
                onAttachmentsRejected: () => {
                    setText(draftText);
                    attachments.restoreRejectedAttachments(preparedAttachments);
                },
            },
        );
        if (isAccepted) {
            setText('');
            attachments.clearAttachments();
        }
    };

    return {
        ...attachments,
        text,
        textareaRef,
        isDisabled,
        isCounterVisible: text.length >= MESSAGE_LENGTH_COUNTER_THRESHOLD,
        isTextTooLong,
        canSend,
        changeText: setText,
        insertEmoji: emoji => {
            const textarea = textareaRef.current;
            const insertion = insertAtSelection(
                text,
                textarea?.selectionStart ?? text.length,
                textarea?.selectionEnd ?? text.length,
                emoji,
            );
            flushSync(() => setText(insertion.text));
            textarea?.focus();
            textarea?.setSelectionRange(insertion.caret, insertion.caret);
        },
        send,
        handleKeyDown: event => {
            if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
            event.preventDefault();
            void send();
        },
        handlePaste: event => {
            if (isDisabled || event.clipboardData.getData('text/plain') !== '') return;
            const files = getClipboardFiles(event.clipboardData);
            if (files.length === 0) return;
            event.preventDefault();
            attachments.attachFiles(files);
        },
    };
}

function getClipboardFiles(clipboardData: DataTransfer): File[] {
    const files = Array.from(clipboardData.files);
    if (files.length > 0) return files;

    return Array.from(clipboardData.items ?? [])
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile())
        .filter((file): file is File => file !== null);
}
